
import PropTypes from 'prop-types';
import { useEffect, useCallback, useState } from 'react';
// routes
import { paths } from 'src/routes/paths';
import { useRouter, useSearchParams } from 'src/routes/hooks';
//
import Box from "@mui/material/Box";
import { useAuthContext } from '../hooks';

import axios from "../../utils/axios";
import { getShopIdFromPathname } from 'src/utils/url-utils';
import { IS_DEV } from 'src/config-global';

// === Max detection (локально, чтобы не трогать остальную логику) ===
const max = typeof window !== 'undefined' ? window.WebApp : null;
const maxInitData = max?.initData || '';
const isMax = Boolean(maxInitData);

const tg = window?.Telegram?.WebApp;
const tgData = tg?.initDataUnsafe || {};
const initData = tg?.initData || '1234124reaedfadg3546qwer';


// ----------------------------------------------------------------------

const loginPaths = {
  jwt: paths.auth.jwt.login,
};

// ----------------------------------------------------------------------



export default function AuthGuard({ children, userType }) {
  const router = useRouter();
  const getParams = useSearchParams();
  const { isAuthenticated, method, tgLogin, setUserType, loading } = useAuthContext();
  const [checked, setChecked] = useState(false);
  const [loginAttempted, setLoginAttempted] = useState(false);


  // Extract params
  const get_id = getParams.get('id');
  const get_first_name = getParams.get('first_name');
  const get_last_name = getParams.get('last_name');
  const get_username = getParams.get('username');
  const get_photo_url = getParams.get('photo_url');
  const get_auth_date = getParams.get('auth_date');
  const get_hash = getParams.get('hash');
  const tgWebAppStartParam = getParams.get('tgWebAppStartParam');

  let AUTH_GET_HTTP = null;
  if (get_id && get_first_name && get_last_name && get_username && get_photo_url && get_auth_date && get_hash) {
    AUTH_GET_HTTP = {
      id: get_id,
      first_name: get_first_name,
      last_name: get_last_name,
      username: get_username,
      photo_url: get_photo_url,
      auth_date: get_auth_date,
      hash: get_hash
    };
  }

  // Set user type
  useEffect(() => {
    setUserType(userType);
  }, [setUserType, userType]);

  // Main Auth Logic
  const init = useCallback(async () => {
    console.log("AuthGuard: init called", { isAuthenticated, loading, hasAuthData: Object.keys(tgData).length > 0 });

    if (loginAttempted) {
      console.log("AuthGuard: Login already attempted, skipping init");
      return;
    }

    if (isAuthenticated) {
      console.log("AuthGuard: already authenticated, setting checked=true");
      setChecked(true);
      return;
    }

    // Prepare auth data
    const windowlocation = window.location.pathname;
    const shopid = getShopIdFromPathname(windowlocation);
    const emptyTgObj = tgData.hasOwnProperty("user");

    // UTM & Ref parsing
    let refId = null;
    let utm = null;
    const regexpUTM = /_utm([a-zA-Z0-9]+)/gm;
    const regexpRef = /_ref([0-9]+)/gm;
    if (tgWebAppStartParam) {
      const arUtm = [...tgWebAppStartParam.matchAll(regexpUTM)];
      const arRefId = [...tgWebAppStartParam.matchAll(regexpRef)];
      if (arUtm.length > 0 && arUtm[0]?.input) utm = arUtm[0][1];
      if (arRefId.length > 0 && arRefId[0]?.input) refId = arRefId[0][1];
    }

    let authdata = {};
    if (IS_DEV) {
      const probka = {
        id: "111111",
        first_name: "Евгений",
        last_name: "Кудряшов",
        username: "jonnnnster",
        photo_url: "/img",
        auth_date: "12.12.2022",
        hash: "1234124reaedfadg3546qwer"
      };
      authdata = { ...probka };
    }
    if (AUTH_GET_HTTP) authdata = AUTH_GET_HTTP;
    if (emptyTgObj) authdata = { ...tgData };

    // Determine if we have data to login
    const hasAuthData = Object.keys(authdata).length > 0;
    console.log("AuthGuard: hasAuthData", hasAuthData, authdata);

    if (hasAuthData) {
      try {
        console.log("AuthGuard: Attempting auto-login");
        if (isMax) {
          const payload = { platform: 'max', max_response: maxInitData, type: userType, shop_id: shopid };
          if (refId) payload.ref = refId;
          if (utm) payload.utm = utm;
          await tgLogin(payload);
        } else {
          // For Telegram, pass authdata directly. tgLogin will wrap it in tg_response.
          // Note: ref and utm are currently ignored by tgLogin for Telegram case in this implementation.
          await tgLogin(authdata);
        }
        console.log("AuthGuard: Auto-login successful");
      } catch (error) {
        console.error("Auto-login failed:", error);
        // Log error to server if needed
        try {
          await axios.post(`/auth-error`, { payload: authdata, url: windowlocation });
        } catch (e) { /* ignore */ }
      }
    }

    setLoginAttempted(true);
  }, [isAuthenticated, tgLogin, userType, tgWebAppStartParam, AUTH_GET_HTTP, loginAttempted]);

  // Run init when loading is done
  useEffect(() => {
    if (!loading) {
      init();
    }
  }, [loading, init]);

  // Handle redirect or show content
  useEffect(() => {
    if (loading) return;

    console.log("AuthGuard: check effect", { isAuthenticated, loginAttempted });

    if (isAuthenticated) {
      setChecked(true);
    } else if (loginAttempted) {
      console.log("AuthGuard: Redirecting to login");
      // Login attempted (or skipped) and still not authenticated -> Redirect
      const searchParams = new URLSearchParams({
        returnTo: window.location.pathname,
      }).toString();

      const loginPath = loginPaths[method];
      const tgstart = tgWebAppStartParam ? `?tgWebAppStartParam=${tgWebAppStartParam}` : '';
      const href = `${loginPath}?${searchParams}${tgstart}`;

      router.replace(href);
    }
  }, [isAuthenticated, loginAttempted, loading, method, router, tgWebAppStartParam]);

  if (!checked) {
    return null;
  }

  return <>{children}</>;
}

AuthGuard.propTypes = {
  children: PropTypes.node,
  userType: PropTypes.string,
};

// function getShopIdFromPathname(strPathname) {
//     // Шаблоны для извлечения shopid
//     const patterns = [
//         // Пути, где shopid идет сразу после префикса
//         /^\/(?:shop|lp|event|games|product)\/([a-zA-Z0-9]{15,})(?:\/|\?|$)/,
//         // Пути, где shopid идет после dashboard/...
//         /^\/dashboard\/(?:shop|stories|usercard|general|pages|orders|analytics|notify|subscribers|referals|forms|quizzes|integrations|settings|pays)\/([a-zA-Z0-9]{15,})(?:\/|\?|$)/,
//         // Пути, где shopid идет после dashboard/general/...
//         /^\/dashboard\/general\/(?:shop|usercard|usercardchoice|settings|articles|tools|qs|chatbot|goods|pages|payment|paymentManual|promotions|affiliate|paymentAuto)\/([a-zA-Z0-9]{15,})(?:\/|\?|$)/,
//         // Пути, где shopid идет после dashboard/notify/...
//         /^\/dashboard\/notify\/(?:auto\/element\/)?([a-zA-Z0-9]{15,})(?:\/|\?|$)/,
//         // Пути, где shopid идет после product/...
//         /^\/product\/(?:shop|article)\/([a-zA-Z0-9]{15,})(?:\/|\?|$)/,
//         // Новый шаблон для путей вида /dashboard/notify/7CYFCqhCnffSfBdghcOEzZ/list/
//         /^\/dashboard\/notify\/([a-zA-Z0-9]{15,})\/list\//,
//         // Новый шаблон для URL вида /dashboard/forms/answer_comments/...
//         /^\/dashboard\/forms\/answer_comments\/([a-zA-Z0-9]{15,})(?:\/|\?|$)/,
//         // Новый шаблон для URL вида /profile/forms/answer_comments/...
//         /^\/profile\/forms\/answer_comments\/([a-zA-Z0-9]{15,})(?:\/|\?|$)/,
//     ];
//
//     // Перебираем шаблоны и ищем совпадение
//     // eslint-disable-next-line no-restricted-syntax
//     for (const pattern of patterns) {
//         const match = strPathname.match(pattern);
//         if (match) {
//             const shopId = match[1];
//             if (process.env.NODE_ENV === 'development') {
//                 console.log(`Extracted shopId from pathname "${strPathname}":`, shopId);
//             }
//             return shopId;
//         }
//     }
//
//     // Если ничего не найдено
//     if (process.env.NODE_ENV === 'development') {
//         console.log(`No shopId found in pathname "${strPathname}"`);
//     }
//     return "";
// }



function _getShopIdFromPathname(strPathname) {
  const regx = /\/(?:product(?:\/article)?|lp|shop)\/([a-zA-Z0-9]+)(?:\/|\?|$)/
  // /(\/|%2F)(lp|shop)(\/|%2F)([\w\d]+)?/gm

  // /\/lp\/([\w\d]+)/gm
  // console.log("strPathname" , strPathname );
  const arrFindStrShop = strPathname ? [...strPathname.matchAll(regx)] : [];
  let shopid = ""
  if (arrFindStrShop.length > 0) {
    if (arrFindStrShop[0]?.length >= 5) {
      shopid = arrFindStrShop[0][4]
    }
  }
  return shopid

}
